import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize from 'rehype-sanitize'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SEO from '../utils/seo'
import Tag from '../components/Tag'
import ShareButtons from '../components/ShareButtons'
import { formatDate } from '../utils/format'

const API_URL = import.meta.env.VITE_API_URL || '/api'

const markdownComponents = {
  h1: ({ children }) => (
    <h1 className="text-3xl font-bold text-off-white mt-10 mb-4">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-2xl font-bold text-off-white mt-8 mb-4">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-xl font-bold text-off-white mt-6 mb-3">{children}</h3>
  ),
  p: ({ children }) => (
    <p className="text-gray-300 mb-4 leading-relaxed">{children}</p>
  ),
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer"
       className="text-cyan hover:text-cyan/80 gradient-underline">
      {children}
    </a>
  ),
  code: ({ inline, className, children }) => {
    if (inline) {
      return (
        <code className="bg-slate px-1.5 py-0.5 rounded text-cyan text-sm font-mono">
          {children}
        </code>
      )
    }
    return <code className={className}>{children}</code>
  },
  pre: ({ children }) => (
    <pre className="code-block mb-4 custom-scrollbar overflow-x-auto">
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-purple pl-4 my-4 italic text-gray-400">
      {children}
    </blockquote>
  ),
  ul: ({ children }) => (
    <ul className="list-disc list-inside text-gray-300 mb-4 space-y-1">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal list-inside text-gray-300 mb-4 space-y-1">{children}</ol>
  ),
  img: ({ src, alt }) => (
    <img src={src} alt={alt} className="rounded-lg my-6 max-w-full" />
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto mb-4">
      <table className="w-full border-collapse border border-slate">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-slate bg-slate/50 px-4 py-2 text-left text-off-white font-bold">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-slate px-4 py-2 text-gray-300">{children}</td>
  ),
}

const BlogPost = () => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [post, setPost] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchPost()
  }, [slug])

  const fetchPost = async () => {
    try {
      const response = await fetch(`${API_URL}/blog-posts/${slug}`)
      if (!response.ok) {
        navigate('/404', { replace: true })
        return
      }
      const data = await response.json()
      if (data.status !== 'published') {
        navigate('/404', { replace: true })
        return
      }
      setPost(data)
    } catch (error) {
      console.error('Error fetching blog post:', error)
      navigate('/404', { replace: true })
    } finally {
      setLoading(false)
    }
  }

  if (loading || !post) return null

  const currentUrl = `https://promptedthoughts.dev/blog/${post.slug}`

  return (
    <>
      <SEO
        title={post.title}
        description={post.excerpt}
        image={post.coverImage}
        url={currentUrl}
        type="article"
        article={{
          publishedTime: post.publishedAt,
          tags: post.tags,
        }}
      />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="section"
      >
        <div className="container-custom max-w-4xl">
          <Link
            to="/blog"
            className="inline-flex items-center text-cyan hover:text-cyan/80 mb-8 gradient-underline"
          >
            <ArrowBackIcon sx={{ mr: 1 }} /> Back to Blog
          </Link>

          {post.coverImage && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-8 rounded-xl overflow-hidden"
            >
              <img
                src={post.coverImage}
                alt={post.title}
                className="w-full h-64 md:h-96 object-cover"
              />
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mb-8"
          >
            <div className="flex flex-wrap gap-2 mb-4">
              {post.tags.map(tag => (
                <Tag key={tag} tag={tag} />
              ))}
            </div>

            <h1 className="text-3xl md:text-5xl font-bold text-off-white mb-4">
              {post.title}
            </h1>

            <div className="flex items-center gap-3 text-gray-400 mb-6">
              <span>{formatDate(post.publishedAt)}</span>
            </div>

            <ShareButtons url={currentUrl} title={post.title} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw, rehypeSanitize, rehypeHighlight]}
              components={markdownComponents}
            >
              {post.content}
            </ReactMarkdown>
          </motion.div>
        </div>
      </motion.div>
    </>
  )
}

export default BlogPost
